import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { Note, NoteVersion, Reminder } from '@voice/shared';
import { fetchNote, updateNote, reprocessNote, fetchNoteVersions, deleteNote, approveAI, undoAI } from '../services/api';
import type { ApproveAIInput } from '../services/api';
import { useCategories } from '../hooks/useCategories';
import { useToast } from '../hooks/useToast';
import { Toast } from '../components/Toast';
import { ConfidenceBanner } from '../components/ConfidenceBanner';
import { AIReviewCard } from '../components/AIReviewCard';
import { ManualClassifier } from '../components/ManualClassifier';
import { supabase } from '../services/supabase';

export function NoteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { categories } = useCategories();
  const { toast, showError, showSuccess, dismiss } = useToast();

  const [note, setNote] = useState<Note | null>(null);
  const [versions, setVersions] = useState<NoteVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [transcript, setTranscript] = useState('');
  const [saving, setSaving] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [aiActionLoading, setAiActionLoading] = useState(false);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadNote = useCallback(async () => {
    if (!id) return;
    try {
      const n = await fetchNote(id);
      setNote(n);
      // Only update transcript if user hasn't made unsaved edits
      setTranscript((prev) => {
        const serverText = n.edited_transcript ?? n.raw_transcript ?? '';
        // If this is the first load or user hasn't edited, use server value
        if (!prev || !dirty) return serverText;
        return prev;
      });
    } catch {
      showError('Failed to load note');
    }
  }, [id, dirty, showError]);

  const loadVersions = useCallback(async () => {
    if (!id) return;
    try {
      const v = await fetchNoteVersions(id);
      setVersions(v);
    } catch {
      showError('Failed to load versions');
    }
  }, [id, showError]);

  const loadReminders = useCallback(async () => {
    if (!id) return;
    try {
      const { data } = await supabase
        .from('reminders')
        .select('*')
        .eq('note_id', id)
        .order('remind_at', { ascending: true });
      if (data) setReminders(data as Reminder[]);
    } catch {
      // non-fatal
    }
  }, [id]);

  // Initial load
  useEffect(() => {
    async function init() {
      await Promise.all([loadNote(), loadVersions(), loadReminders()]);
      setLoading(false);
    }
    init();
  }, [loadNote, loadVersions, loadReminders]);

  // Poll while processing
  useEffect(() => {
    const isProcessing = note?.status === 'processing' || note?.status === 'transcribing';
    if (!isProcessing) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }

    pollRef.current = setInterval(async () => {
      await loadNote();
      await loadVersions();
    }, 3000);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [note?.status, loadNote, loadVersions]);

  const handleSave = async () => {
    if (!id || !note) return;
    setSaving(true);
    try {
      const updated = await updateNote(id, { edited_transcript: transcript });
      setNote(updated);
      setDirty(false);
      showSuccess('Saved');
    } catch {
      showError('Failed to save');
    }
    setSaving(false);
  };

  const handleReprocess = async () => {
    if (!id) return;

    // Save first if there are unsaved edits
    if (dirty) {
      await handleSave();
    }

    setReprocessing(true);
    try {
      const updated = await reprocessNote(id);
      setNote(updated);
    } catch {
      showError('Failed to reprocess');
    }
    setReprocessing(false);
  };

  const handleDelete = async () => {
    if (!id) return;
    setDeleting(true);
    try {
      await deleteNote(id);
      navigate('/');
    } catch {
      showError('Failed to delete note');
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleTranscriptChange = (value: string) => {
    setTranscript(value);
    setDirty(true);
  };

  const handleUndoAI = async () => {
    if (!id) return;
    setAiActionLoading(true);
    try {
      const updated = await undoAI(id);
      setNote(updated);
      showSuccess('AI results undone');
    } catch {
      showError('Failed to undo AI');
    }
    setAiActionLoading(false);
  };

  const handleApproveAI = async (data: ApproveAIInput) => {
    if (!id) return;
    setAiActionLoading(true);
    try {
      const updated = await approveAI(id, data);
      setNote(updated);
      setShowReview(false);
      showSuccess('AI results approved');
      await loadVersions();
    } catch {
      showError('Failed to approve AI');
    }
    setAiActionLoading(false);
  };

  const handleManualClassify = async (data: { intent: string; category_id: string | null }) => {
    if (!id) return;
    setAiActionLoading(true);
    try {
      const updated = await updateNote(id, {
        intent: data.intent,
        category_id: data.category_id,
        confidence: 1.0,
      });
      setNote(updated);
      showSuccess('Classification saved');
    } catch {
      showError('Failed to save classification');
    }
    setAiActionLoading(false);
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner} />
        </div>
      </div>
    );
  }

  if (!note) {
    return (
      <div style={styles.container}>
        <button style={styles.backBtn} onClick={() => navigate('/')} type="button">
          &larr; Back
        </button>
        <p style={styles.errorText}>Note not found</p>
      </div>
    );
  }

  const isProcessing = note.status === 'processing' || note.status === 'transcribing';
  const category = note.category_id
    ? categories.find((c) => c.id === note.category_id)
    : undefined;

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <button style={styles.backBtn} onClick={() => navigate('/')} type="button">
          &larr; Back
        </button>
        <StatusBadge status={note.status} />
        <span style={styles.timeAgo}>{formatRelativeTime(note.created_at)}</span>
      </div>

      {/* Summary + intent */}
      {note.summary && <p style={styles.summary}>{note.summary}</p>}
      <div style={styles.badgeRow}>
        {note.intent && <span style={styles.intentBadge}>{note.intent}</span>}
        {category && (
          <span style={{
            ...styles.categoryBadge,
            borderColor: category.color ?? 'var(--color-border)',
            color: category.color ?? 'var(--color-text-secondary)',
          }}>
            {category.name}
          </span>
        )}
      </div>

      {/* Confidence banner */}
      {!isProcessing && note.status === 'ready' && (
        <ConfidenceBanner
          note={note}
          onUndo={handleUndoAI}
          onReviewClick={() => setShowReview(true)}
        />
      )}

      {/* AI Review Card (medium confidence) */}
      {showReview && note.confidence != null && note.confidence >= 0.60 && note.confidence < 0.80 && (
        <AIReviewCard
          note={note}
          latestVersion={versions[0] ?? null}
          categories={categories}
          onApprove={handleApproveAI}
          loading={aiActionLoading}
        />
      )}

      {/* Manual Classifier (low confidence) */}
      {!isProcessing && note.confidence != null && note.confidence < 0.60 && (
        <ManualClassifier
          categories={categories}
          onSave={handleManualClassify}
          loading={aiActionLoading}
        />
      )}

      {/* Reminders */}
      {reminders.length > 0 && (
        <div style={styles.section}>
          <label style={styles.label}>Reminders</label>
          {reminders.map((r) => (
            <div key={r.id} style={styles.reminderRow}>
              <span style={styles.reminderIcon}>&#128276;</span>
              <span style={styles.reminderDate}>
                {new Date(r.remind_at).toLocaleDateString(undefined, {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                })}
              </span>
              {r.triggered && <span style={styles.triggeredBadge}>past</span>}
            </div>
          ))}
        </div>
      )}

      {/* Processing indicator */}
      {isProcessing && (
        <div style={styles.processingBanner}>
          <div style={styles.spinnerSmall} />
          <span style={styles.processingText}>
            {note.status === 'transcribing' ? 'Transcribing...' : 'Processing...'}
          </span>
        </div>
      )}

      {/* Transcript editor */}
      <div style={styles.section}>
        <label style={styles.label}>Transcript</label>
        <textarea
          style={styles.textarea}
          value={transcript}
          onChange={(e) => handleTranscriptChange(e.target.value)}
          disabled={isProcessing}
          rows={8}
          placeholder="No transcript yet..."
        />
      </div>

      {/* Action bar */}
      <div style={styles.actionBar}>
        <button
          style={{
            ...styles.saveBtn,
            opacity: dirty && !isProcessing ? 1 : 0.5,
          }}
          onClick={handleSave}
          disabled={!dirty || saving || isProcessing}
          type="button"
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button
          style={{
            ...styles.reprocessBtn,
            opacity: isProcessing ? 0.5 : 1,
          }}
          onClick={handleReprocess}
          disabled={isProcessing || reprocessing || !transcript}
          type="button"
        >
          {reprocessing || isProcessing ? 'Processing...' : 'Re-run AI'}
        </button>
        <button
          style={{
            ...styles.deleteBtn,
            opacity: isProcessing ? 0.5 : 1,
          }}
          onClick={() => setShowDeleteConfirm(true)}
          disabled={isProcessing || deleting}
          type="button"
        >
          Delete
        </button>
      </div>

      {/* Delete confirmation */}
      {showDeleteConfirm && (
        <div style={styles.confirmOverlay}>
          <div style={styles.confirmDialog}>
            <p style={styles.confirmText}>Delete this note? This cannot be undone.</p>
            <div style={styles.confirmActions}>
              <button
                style={styles.cancelBtn}
                onClick={() => setShowDeleteConfirm(false)}
                type="button"
              >
                Cancel
              </button>
              <button
                style={styles.confirmDeleteBtn}
                onClick={handleDelete}
                disabled={deleting}
                type="button"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Version history */}
      <div style={styles.section}>
        <button
          style={styles.versionsToggle}
          onClick={() => setShowVersions(!showVersions)}
          type="button"
        >
          <span>Version history ({versions.length})</span>
          <span>{showVersions ? '\u25B2' : '\u25BC'}</span>
        </button>
        {showVersions && (
          <div style={styles.versionsList}>
            {versions.length === 0 ? (
              <p style={styles.noVersions}>No versions yet</p>
            ) : (
              versions.map((v) => (
                <VersionCard key={v.id} version={v} />
              ))
            )}
          </div>
        )}
      </div>

      {/* Toast */}
      {toast && <Toast message={toast.message} type={toast.type} onDismiss={dismiss} />}
    </div>
  );
}

function VersionCard({ version }: { version: NoteVersion }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div style={styles.versionCard}>
      <button
        style={styles.versionHeader}
        onClick={() => setExpanded(!expanded)}
        type="button"
      >
        <span style={styles.versionNum}>v{version.version}</span>
        <span style={styles.versionTime}>{formatRelativeTime(version.created_at)}</span>
        {version.intent && <span style={styles.versionIntent}>{version.intent}</span>}
      </button>
      {expanded && (
        <div style={styles.versionBody}>
          {version.summary && (
            <p style={styles.versionSummary}>{version.summary}</p>
          )}
          {version.transcript && (
            <p style={styles.versionTranscript}>{version.transcript}</p>
          )}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colorMap: Record<string, string> = {
    ready: '#22c55e',
    error: 'var(--color-danger)',
    processing: 'var(--color-accent)',
    transcribing: 'var(--color-accent)',
  };
  const color = colorMap[status] ?? 'var(--color-text-muted)';

  return (
    <span
      style={{
        fontSize: 'var(--font-size-xs)',
        color,
        fontWeight: 500,
        textTransform: 'capitalize' as const,
      }}
    >
      {status}
    </span>
  );
}

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column' as const,
    flex: 1,
    padding: 'var(--space-4)',
    gap: 'var(--space-4)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-3)',
  },
  backBtn: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-accent)',
    padding: '4px 8px',
    borderRadius: 'var(--radius-sm)',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    fontWeight: 500,
  },
  timeAgo: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-muted)',
    marginLeft: 'auto',
  },
  summary: {
    fontSize: 'var(--font-size-sm)',
    fontWeight: 500,
    color: 'var(--color-text-primary)',
    lineHeight: 1.4,
  },
  badgeRow: {
    display: 'flex',
    gap: 'var(--space-2)',
    flexWrap: 'wrap' as const,
  },
  intentBadge: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-accent)',
    backgroundColor: 'var(--color-accent-light)',
    padding: '2px 8px',
    borderRadius: 'var(--radius-full)',
    fontWeight: 500,
  },
  categoryBadge: {
    fontSize: 'var(--font-size-xs)',
    padding: '2px 8px',
    borderRadius: 'var(--radius-full)',
    border: '1px solid',
    fontWeight: 500,
  },
  reminderRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-2)',
    padding: 'var(--space-2) var(--space-3)',
    backgroundColor: 'var(--color-warning-light)',
    borderRadius: 'var(--radius-md)',
  },
  reminderIcon: {
    fontSize: 'var(--font-size-sm)',
  },
  reminderDate: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-primary)',
    fontWeight: 500,
    flex: 1,
  },
  triggeredBadge: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-muted)',
    padding: '1px 6px',
    borderRadius: 'var(--radius-full)',
    border: '1px solid var(--color-border)',
  },
  processingBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-2)',
    padding: 'var(--space-3)',
    backgroundColor: 'var(--color-accent-light)',
    borderRadius: 'var(--radius-md)',
  },
  processingText: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-accent)',
    fontWeight: 500,
  },
  section: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 'var(--space-2)',
  },
  label: {
    fontSize: 'var(--font-size-xs)',
    fontWeight: 600,
    color: 'var(--color-text-secondary)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  },
  textarea: {
    width: '100%',
    padding: 'var(--space-3)',
    fontSize: 'var(--font-size-sm)',
    lineHeight: 1.6,
    color: 'var(--color-text-primary)',
    backgroundColor: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    resize: 'vertical' as const,
    fontFamily: 'inherit',
    boxSizing: 'border-box' as const,
  },
  actionBar: {
    display: 'flex',
    gap: 'var(--space-3)',
  },
  saveBtn: {
    flex: 1,
    padding: 'var(--space-3)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    color: 'var(--color-text-primary)',
    backgroundColor: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
  },
  reprocessBtn: {
    flex: 1,
    padding: 'var(--space-3)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    color: '#fff',
    backgroundColor: 'var(--color-accent)',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
  },
  versionsToggle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    padding: 'var(--space-3)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 500,
    color: 'var(--color-text-secondary)',
    backgroundColor: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
  },
  versionsList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 'var(--space-2)',
  },
  noVersions: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-muted)',
    textAlign: 'center' as const,
    padding: 'var(--space-3)',
  },
  versionCard: {
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    overflow: 'hidden',
  },
  versionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-2)',
    width: '100%',
    padding: 'var(--space-3)',
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-secondary)',
    backgroundColor: 'var(--color-surface)',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left' as const,
  },
  versionNum: {
    fontWeight: 600,
    color: 'var(--color-text-primary)',
  },
  versionTime: {
    flex: 1,
    color: 'var(--color-text-muted)',
  },
  versionIntent: {
    color: 'var(--color-accent)',
    fontWeight: 500,
  },
  versionBody: {
    padding: 'var(--space-3)',
    borderTop: '1px solid var(--color-border)',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 'var(--space-2)',
  },
  versionSummary: {
    fontSize: 'var(--font-size-sm)',
    fontWeight: 500,
    color: 'var(--color-text-primary)',
    lineHeight: 1.4,
  },
  versionTranscript: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-secondary)',
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap' as const,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: 'var(--space-8)',
  },
  spinner: {
    width: '24px',
    height: '24px',
    border: '2px solid var(--color-border)',
    borderTopColor: 'var(--color-accent)',
    borderRadius: '50%',
    animation: 'spin 0.6s linear infinite',
  },
  spinnerSmall: {
    width: '16px',
    height: '16px',
    border: '2px solid var(--color-border)',
    borderTopColor: 'var(--color-accent)',
    borderRadius: '50%',
    animation: 'spin 0.6s linear infinite',
  },
  errorText: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-danger)',
    textAlign: 'center' as const,
  },
  deleteBtn: {
    padding: 'var(--space-3)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    color: 'var(--color-danger)',
    backgroundColor: 'transparent',
    border: '1px solid var(--color-danger)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
  },
  confirmOverlay: {
    position: 'fixed' as const,
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100,
    padding: 'var(--space-4)',
  },
  confirmDialog: {
    backgroundColor: 'var(--color-surface-elevated)',
    borderRadius: 'var(--radius-lg)',
    padding: 'var(--space-4)',
    maxWidth: '320px',
    width: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 'var(--space-4)',
  },
  confirmText: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-primary)',
    lineHeight: 1.5,
    textAlign: 'center' as const,
  },
  confirmActions: {
    display: 'flex',
    gap: 'var(--space-3)',
  },
  cancelBtn: {
    flex: 1,
    padding: 'var(--space-3)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    color: 'var(--color-text-primary)',
    backgroundColor: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
  },
  confirmDeleteBtn: {
    flex: 1,
    padding: 'var(--space-3)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    color: '#fff',
    backgroundColor: 'var(--color-danger)',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
  },
};
